import { useInfiniteQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { CONVERSATION_TYPES } from 'store/smsDashboard';

import type {
  ConversationType,
  IConversationsListRes,
} from 'infrastructure/interfaces';

interface Props {
  type: ConversationType;
  cursor?: Date;
  read: string;
  closed: string;
  assignedToMe: boolean;
  unenrolled: boolean;
  withoutAssignedCcmSpecialist: boolean;
  lastMessageDate: string;
  searchQuery: string;
}

const PAGE_SIZE = 10;
const API_CONVERSATIONS = 'core/conversations';
const API_UNREGISTERED_CONVERSATIONS = 'core/conversations-unregistered';

export const getSmsDashboardConversationsFn = async (props: Props) => {
  const isUnregisteredTabs =
    props.type === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    props.type === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const url = isUnregisteredTabs
    ? API_UNREGISTERED_CONVERSATIONS
    : API_CONVERSATIONS;

  const { data, error } = await dataService.getList<IConversationsListRes>(
    apiUrlGenerator(url, {
      ...props,
      pageSize: props.searchQuery ? 10000 : PAGE_SIZE,
    }),
  );

  if (error) {
    showResult('Unable to retrieve conversations');
    throw new Error(error);
  }
  return data;
};

interface IKeyProps {
  type: ConversationType;
  read: string;
  closed: string;
  assignedToMe?: boolean;
  lastMessageDate?: string;
  searchQuery?: string;
  onlyNeedFilters?: boolean;
  unenrolled?: boolean;
  withoutAssignedCcmSpecialist?: boolean;
}

export const getSmsDashboardConversationsKey = ({
  type,
  read,
  assignedToMe,
  unenrolled,
  withoutAssignedCcmSpecialist,
  closed,
  lastMessageDate,
  searchQuery,
  onlyNeedFilters,
}: IKeyProps) => {
  return onlyNeedFilters
    ? ['getSmsDashboardConversations', type, read, closed]
    : [
        'getSmsDashboardConversations',
        type,
        read,
        closed,
        assignedToMe,
        unenrolled,
        withoutAssignedCcmSpecialist,
        lastMessageDate,
        searchQuery,
      ];
};

export const useSmsDashboardConversations = (props: Props) => {
  const {
    data: conversations,
    refetch,
    isFetching,
    isLoading,
    isRefetching,
    ...other
  } = useInfiniteQuery({
    queryKey: getSmsDashboardConversationsKey({
      type: props.type,
      read: props.read,
      closed: props.closed,
      assignedToMe: props.assignedToMe,
      unenrolled: props.unenrolled,
      withoutAssignedCcmSpecialist: props.withoutAssignedCcmSpecialist,
      lastMessageDate: props.lastMessageDate,
      searchQuery: props.searchQuery,
    }),
    queryFn: async ({ pageParam = 0 }) =>
      getSmsDashboardConversationsFn({
        ...props,
        cursor:
          pageParam ||
          (props.lastMessageDate === 'oldest' ? undefined : new Date()),
      }),
    getNextPageParam: (lastPage) => {
      return lastPage?.hasMore
        ? lastPage.items[lastPage.items.length - 1]?.lastMessageDate
        : false;
    },
  });

  return {
    conversations: conversations?.pages.flat(),
    fetchConversations: refetch,
    conversationsLoading: isLoading || isFetching || isRefetching,
    isLoading,
    isFetching,
    ...other,
  };
};
